import { css } from '@emotion/react';
import { noop } from 'lodash';
import { type HTMLProps, type KeyboardEventHandler, memo, useCallback, useRef } from 'react';

import type { LessonStudentNote as LessonStudentNoteShape } from '@didaskill/domain/lesson-notes/types';
import { ContentEditable, Typography, WaveCard } from '@didaskill/ui/design-system';
import { FormattedRelativeTimeFromNow, useBoolState } from '@didaskill/ui/utils';

export type LessonStudentNoteProps = Omit<HTMLProps<HTMLDivElement>, 'ref'> & {
  note?: Pick<LessonStudentNoteShape, 'id' | 'content' | 'blocks' | 'type'> & {
    createdAt?: string;
  };
  /** Changes card appearance for highlighted notes. */
  highlighted?: boolean;
  /** Trigger note group highlight. */
  onHighlightBlock?: (blockId: string) => void;
  /** Callback on save. */
  onSave?: (note: Pick<LessonStudentNoteShape, 'id' | 'content' | 'blocks' | 'type'>) => void;
};

export const LessonStudentNoteBase = ({
  note = null,
  highlighted = false,
  onHighlightBlock = noop,
  onSave = noop,
  ...props
}: LessonStudentNoteProps) => {
  const [isEditable, onStartEditing, onStopEditing] = useBoolState(!note?.content);
  const hoverTimeout = useRef<ReturnType<typeof setTimeout>>(null);

  const noteBlockIds = Object.keys(note.blocks); // TODO: refacto when we add highlights

  const onFocusKeyDown: KeyboardEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      if (!isEditable && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        onStartEditing();
      }
    },
    [onStartEditing, isEditable],
  );

  const onClick = useCallback(() => {
    onHighlightBlock(noteBlockIds[0]); // TODO: refacto when we add highlights
    onStartEditing();
  }, [onHighlightBlock, onStartEditing, noteBlockIds[0]]);

  const onFinishEditing = useCallback(
    ({ target: { innerText } }: { target: { innerText: string } }) => {
      onSave({
        ...note,
        content: innerText,
      });
      onStopEditing();
    },
    [note, onSave, onStopEditing],
  );

  const onKeyDown: KeyboardEventHandler<HTMLElement> = useCallback(
    ({ key, target }) => {
      if (key === 'Enter') {
        onFinishEditing({ target: target as HTMLElement });
      }
    },
    [onFinishEditing],
  );

  // Highlight note's block if user hovers for 1 second.
  const onStartHovering = useCallback(() => {
    hoverTimeout.current = setTimeout(() => onHighlightBlock(noteBlockIds[0]), 1000);
  }, [onHighlightBlock, noteBlockIds[0]]);

  const onStopHovering = useCallback(() => clearTimeout(hoverTimeout.current), []);

  return (
    <div
      role="button"
      onClick={onClick}
      onKeyDown={onFocusKeyDown}
      onMouseEnter={onStartHovering}
      onMouseLeave={onStopHovering}
      tabIndex={0}
      css={css`
        width: 100%;
      `}
      {...props}
    >
      <WaveCard variant={highlighted ? WaveCard.VARIANTS.PRIMARY_DARK : WaveCard.VARIANTS.PRIMARY}>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 11px;

            padding-bottom: ${note?.content ? 15 : 0}px;
          `}
        >
          {!!note?.createdAt && (
            <Typography
              variant={Typography.VARIANTS.BODY_SMALL_BOLD}
              css={(theme) => css`
                color: ${highlighted ? theme.colors.gray[0] : theme.colors.primary[500]};
                z-index: 1;
              `}
            >
              <FormattedRelativeTimeFromNow date={note.createdAt} />
            </Typography>
          )}

          <ContentEditable
            variant={Typography.VARIANTS.BODY_MEDIUM_REGULAR}
            value={note?.content}
            onKeyDown={onKeyDown}
            onBlur={onFinishEditing}
            disabled={!isEditable}
            css={(theme) => css`
              outline: none;
              color: ${highlighted ? theme.colors.gray[0] : theme.colors.gray[700]};
              z-index: 1;
            `}
            data-cy="note"
          />
        </div>
      </WaveCard>
    </div>
  );
};

LessonStudentNoteBase.displayName = 'LessonStudentNote';

export const LessonStudentNote = memo(LessonStudentNoteBase);
